import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Send,
  CheckCircle,
  MessageCircle,
} from 'lucide-react';
import GlassCard from './GlassCard';

const Contact = ({ lang = 'en' }) => {
  const isAr = lang === 'ar';

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Content Dictionary
  const content = {
    title: isAr ? 'تواصل معنا' : 'Get In Touch',
    subtitle: isAr
      ? 'اتصل بنا للحصول على خدمات غسيل السيارات المتنقلة الراقية في الكويت'
      : 'Contact us for super classy mobile car wash services in Kuwait',
    infoTitle: isAr ? 'معلومات التواصل' : 'Contact Information',
    formTitle: isAr ? 'أرسل لنا رسالة' : 'Send us a Message',
    labels: {
      name: isAr ? 'الاسم *' : 'Your Name *',
      phone: isAr ? 'رقم الهاتف *' : 'Phone Number *',
      email: isAr ? 'البريد الإلكتروني' : 'Email Address',
      service: isAr ? 'اختر الخدمة' : 'Select a service',
      message: isAr ? 'رسالتك *' : 'Your Message *',
      submit: isAr ? 'إرسال عبر واتساب' : 'Send via WhatsApp',
    },
    success: {
      title: isAr ? 'الرسالة جاهزة!' : 'Message Ready!',
      desc: isAr
        ? 'اضغط على زر الإرسال للمتابعة عبر واتساب.'
        : 'Click the button to continue via WhatsApp.',
    },
    instantBooking: isAr ? 'حجز فوري' : 'Instant Booking',
    waResponse: isAr
      ? 'احصل على رد سريع عبر واتساب'
      : 'Get quick response on WhatsApp',
  };

  const contactInfo = [
    {
      icon: <Phone className='h-6 w-6 text-white' />,
      title: isAr ? 'اتصل بنا' : 'Call Us',
      value: '+965 5004 0026',
      action: () => window.open('tel:+96550040026'),
    },
    {
      icon: <Mail className='h-6 w-6 text-white' />,
      title: isAr ? 'البريد الإلكتروني' : 'Email',
      value: 'superclassykw@gmail.com',
      action: () => window.open('mailto:superclassykw@gmail.com'),
    },
    {
      icon: <MapPin className='h-6 w-6 text-white' />,
      title: isAr ? 'مناطق الخدمة' : 'Service Area',
      value: isAr ? 'جميع مناطق الكويت' : 'All Kuwait Areas',
      action: null,
    },
    {
      icon: <Clock className='h-6 w-6 text-white' />,
      title: isAr ? 'ساعات العمل' : 'Working Hours',
      value: isAr ? '١٠ صباحاً - ١٠ مساءً يومياً' : '10 AM - 10 PM Daily',
      action: null,
    },
  ];

  const services = isAr
    ? [
        'استفسار عام',
        'غسيل فوم',
        'تلميع واكس',
        'تنظيف داخلي',
        'تنظيف محرك',
        'نانو سيراميك',
        'باقة خاصة',
      ]
    : [
        'General Inquiry',
        'Foam Wash',
        'Wax Polish',
        'Interior Cleaning',
        'Engine Cleaning',
        'Ceramic Coating',
        'Special Package',
      ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const whatsappMsg = isAr
      ? `مرحباً سوبر كلاسيكي، أود الاستفسار عن خدماتكم:%0Aالاسم: ${formData.name}%0Aالهاتف: ${formData.phone}%0Aالخدمة: ${formData.service}%0Aالرسالة: ${formData.message}`
      : `Hi Super Classy, I want to inquire about your services:%0AName: ${formData.name}%0APhone: ${formData.phone}%0AService: ${formData.service}%0AMessage: ${formData.message}`;
    window.open(`https://wa.me/96550040026?text=${whatsappMsg}`, '_blank');
    setIsSubmitting(false);
    setIsSubmitted(true);
    setTimeout(() => setIsSubmitted(false), 5000);
  };

  return (
    <section
      id='contact'
      className='section-padding py-20'
      dir={isAr ? 'rtl' : 'ltr'}
    >
      <div className='max-w-7xl mx-auto'>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className='text-center mb-12'
        >
          <h2 className='text-4xl md:text-5xl font-black mb-4 text-gradient'>
            {content.title}
          </h2>
          <p className='text-gray-400 max-w-2xl mx-auto font-medium'>
            {content.subtitle}
          </p>
        </motion.div>

        <div className='grid lg:grid-cols-2 gap-8'>
          <div className='space-y-6'>
            <h3 className='text-2xl font-black mb-6'>{content.infoTitle}</h3>

            {/* Info Grid - Matches Screenshot */}
            <div className='grid sm:grid-cols-2 gap-4'>
              {contactInfo.map((info, idx) => (
                <GlassCard
                  key={idx}
                  className={`p-6 border border-white/5 bg-white/5 hover:bg-white/10 transition-all ${info.action ? 'cursor-pointer' : ''}`}
                  onClick={info.action || undefined}
                >
                  <div
                    className={`flex items-center gap-4 ${isAr ? 'flex-row-reverse text-right' : ''}`}
                  >
                    <div className='p-3 premium-gradient rounded-xl shadow-lg'>
                      {info.icon}
                    </div>
                    <div className='overflow-hidden'>
                      <h4 className='font-bold text-white mb-0.5'>
                        {info.title}
                      </h4>
                      <p className='text-gray-400 text-sm truncate font-medium'>
                        {info.value}
                      </p>
                    </div>
                  </div>
                </GlassCard>
              ))}
            </div>

            {/* Instant Booking Banner - Matches Screenshot */}
            <GlassCard className='p-6 bg-green-500/5 border border-green-500/20'>
              <div
                className={`flex flex-col md:flex-row items-center justify-between gap-6 ${isAr ? 'md:flex-row-reverse' : ''}`}
              >
                <div className={isAr ? 'text-right' : 'text-left'}>
                  <h4 className='text-xl font-black text-white mb-1'>
                    {content.instantBooking}
                  </h4>
                  <p className='text-gray-400 text-sm font-medium'>
                    {content.waResponse}
                  </p>
                </div>
                <button
                  onClick={() =>
                    window.open('https://wa.me/96550040026', '_blank')
                  }
                  className='flex items-center gap-2 px-8 py-3 bg-[#25D366] hover:bg-[#20ba5a] text-white rounded-xl font-black transition-all shadow-xl shadow-green-500/20 active:scale-95'
                >
                  <MessageCircle className='h-5 w-5' />{' '}
                  {isAr ? 'دردشة عبر واتساب' : 'Chat on WhatsApp'}
                </button>
              </div>
            </GlassCard>
          </div>

          {/* Form Section */}
          <div className='lg:pl-8'>
            <GlassCard className='p-8 border border-white/10'>
              <h3 className='text-2xl font-black mb-6'>{content.formTitle}</h3>
              {isSubmitted ? (
                <div className='text-center py-12'>
                  <CheckCircle className='h-16 w-16 text-green-500 mx-auto mb-4 animate-bounce' />
                  <h4 className='text-2xl font-black text-white mb-2'>
                    {content.success.title}
                  </h4>
                  <p className='text-gray-400 font-medium'>
                    {content.success.desc}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className='space-y-4'>
                  <div className='grid sm:grid-cols-2 gap-4'>
                    <input
                      type='text'
                      name='name'
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder={content.labels.name}
                      className='w-full px-5 py-4 bg-white/5 border border-white/10 rounded-xl focus:border-royal-purple-500 outline-none text-white transition-all'
                    />
                    <input
                      type='tel'
                      name='phone'
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder={content.labels.phone}
                      className='w-full px-5 py-4 bg-white/5 border border-white/10 rounded-xl focus:border-royal-purple-500 outline-none text-white transition-all'
                    />
                  </div>
                  <input
                    type='email'
                    name='email'
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={content.labels.email}
                    className='w-full px-5 py-4 bg-white/5 border border-white/10 rounded-xl focus:border-royal-purple-500 outline-none text-white transition-all'
                  />
                  <select
                    name='service'
                    value={formData.service}
                    onChange={handleChange}
                    className='w-full px-5 py-4 bg-white/5 border border-white/10 rounded-xl focus:border-royal-purple-500 outline-none text-gray-400 transition-all'
                  >
                    <option value='' className='bg-gray-900'>
                      {content.labels.service}
                    </option>
                    {services.map((s, i) => (
                      <option key={i} value={s} className='bg-gray-900'>
                        {s}
                      </option>
                    ))}
                  </select>
                  <textarea
                    name='message'
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={3}
                    placeholder={content.labels.message}
                    className='w-full px-5 py-4 bg-white/5 border border-white/10 rounded-xl focus:border-royal-purple-500 outline-none text-white transition-all resize-none'
                  />
                  <button
                    type='submit'
                    className='w-full py-5 premium-gradient text-white rounded-xl font-black text-sm uppercase tracking-widest shadow-xl shadow-royal-purple-500/20 active:scale-95 transition-all'
                  >
                    <Send className='h-4 w-4 inline mr-2' />{' '}
                    {content.labels.submit}
                  </button>
                </form>
              )}
            </GlassCard>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
